import { useMemo, useState } from 'react';
import {
  Box, Stack, TextField, InputAdornment, Chip, Typography, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
  Tooltip, IconButton, Alert, Skeleton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { useNavigate } from 'react-router-dom';
import { useGetTatMerchantsQuery } from '../../../../redux/tatApiSlice';
import PillFilters from '../../../AppointmentDeliveriesV2/shared/PillFilters';

const PILLS = [
  { key: 'all',     label: 'All' },
  { key: 'pending', label: 'Pending Setup' },
  { key: 'live',    label: 'TAT Live' },
];

const AVATAR_COLORS = ['#1565c0','#6a1b9a','#00695c','#e65100','#37474f','#4527a0','#2e7d32'];
const avatarColor = (name = '') => AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');

const TH = ({ children, align = 'left', width }) => (
  <TableCell
    align={align}
    sx={{
      width,
      fontWeight: 700,
      fontSize: 11,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      color: 'text.secondary',
      bgcolor: (t) => alpha(t.palette.grey[500], 0.04),
      borderBottom: '2px solid',
      borderColor: 'divider',
      py: 1.25,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </TableCell>
);

function SkeletonRows({ n = 5 }) {
  return Array.from({ length: n }).map((_, i) => (
    <TableRow key={i}>
      <TableCell>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Skeleton variant="circular" width={34} height={34} />
          <Box>
            <Skeleton variant="text" width={130} height={18} />
            <Skeleton variant="text" width={100} height={13} />
          </Box>
        </Stack>
      </TableCell>
      <TableCell><Skeleton variant="text" width={100} /></TableCell>
      <TableCell align="center"><Skeleton variant="rounded" width={36} height={24} sx={{ mx: 'auto' }} /></TableCell>
      <TableCell><Skeleton variant="rounded" width={90} height={24} sx={{ borderRadius: 12 }} /></TableCell>
      <TableCell align="right"><Skeleton variant="circular" width={32} height={32} sx={{ ml: 'auto' }} /></TableCell>
    </TableRow>
  ));
}

export default function TatDashboard({ defaultFilter = 'all' }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState(defaultFilter);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const { data: apiData, isLoading, isError } = useGetTatMerchantsQuery(
    filter !== 'all' ? { tat_status: filter } : {},
  );
  const merchants = apiData?.data || [];

  const counts = useMemo(() => ({
    all:     merchants.length,
    pending: merchants.filter((m) => m.tat_status === 'pending').length,
    live:    merchants.filter((m) => m.tat_status === 'live').length,
  }), [merchants]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return query
      ? merchants.filter((m) => m.name.toLowerCase().includes(query) || m.oid.toLowerCase().includes(query))
      : merchants;
  }, [merchants, q]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>

      {/* Toolbar */}
      <Stack direction="row" alignItems="center" flexWrap="wrap" rowGap={1} sx={{ mb: 2 }}>
        <PillFilters
          pills={PILLS}
          value={filter}
          onChange={(v) => { setFilter(v); setPage(0); }}
          counts={counts}
        />
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search merchant or OID…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 17, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260, '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: 'background.paper' } }}
        />
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          Failed to load merchants. Please refresh and try again.
        </Alert>
      )}

      {/* Table card */}
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          boxShadow: '0 1px 6px rgba(0,0,0,0.06)',
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TH>Merchant</TH>
                <TH>Pricing Committed</TH>
                <TH align="center" width={130}>Account Codes</TH>
                <TH>TAT Status</TH>
                <TH align="right" width={60} />
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <SkeletonRows n={6} />
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {q ? <>No merchants match "<b>{q}</b>"</> : 'No merchants found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((m) => {
                  const isPending = m.tat_status === 'pending';
                  return (
                    <TableRow
                      key={m.oid}
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.025) },
                        cursor: 'default',
                      }}
                    >
                      {/* Merchant */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 700, bgcolor: avatarColor(m.name), color: '#fff' }}>
                            {initials(m.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>{m.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'JetBrains Mono, monospace', fontSize: 10 }}>
                              {m.oid}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>

                      {/* Pricing committed */}
                      <TableCell sx={{ py: 1.5 }}>
                        {m.pricing_done ? (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                              {new Date(m.pricing_done).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </Typography>
                          </Stack>
                        ) : (
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <RadioButtonUncheckedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                            <Typography variant="body2" sx={{ color: 'text.disabled' }}>—</Typography>
                          </Stack>
                        )}
                      </TableCell>

                      {/* Account codes */}
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 32, height: 26, px: 1.25, borderRadius: 1, bgcolor: (t) => alpha(t.palette.grey[500], 0.1) }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>{m.account_codes ?? '—'}</Typography>
                        </Box>
                      </TableCell>

                      {/* TAT status */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Stack spacing={0.25} alignItems="flex-start">
                          <Chip
                            size="small"
                            label={isPending ? 'Pending Setup' : 'TAT Live'}
                            sx={{
                              height: 24, fontWeight: 700, fontSize: 11.5,
                              ...(isPending ? {
                                bgcolor: (t) => alpha(t.palette.warning.main, 0.1),
                                color: 'warning.dark',
                                border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.3)}`,
                              } : {
                                bgcolor: (t) => alpha(t.palette.success.main, 0.1),
                                color: 'success.dark',
                                border: (t) => `1px solid ${alpha(t.palette.success.main, 0.3)}`,
                              }),
                            }}
                          />
                          {!isPending && m.last_updated && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 10, pl: 0.25 }}>
                              Updated {new Date(m.last_updated).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      {/* Action — icon button */}
                      <TableCell align="right" sx={{ py: 1.5, pr: 2 }}>
                        <Tooltip title={isPending ? 'Set up TAT' : 'Update TAT'} arrow>
                          <IconButton
                            size="small"
                            onClick={() => navigate(isPending
                              ? `/merchant-config/tat-setup/${m.oid}`
                              : `/merchant-config/tat-update/${m.oid}`)}
                            sx={{
                              border: 1,
                              borderColor: isPending ? 'primary.main' : 'divider',
                              bgcolor: isPending ? 'primary.main' : 'background.paper',
                              color: isPending ? '#fff' : 'text.secondary',
                              width: 32,
                              height: 32,
                              '&:hover': {
                                bgcolor: isPending
                                  ? 'primary.dark'
                                  : (t) => alpha(t.palette.primary.main, 0.08),
                                borderColor: 'primary.main',
                                color: isPending ? '#fff' : 'primary.main',
                              },
                            }}
                          >
                            {isPending
                              ? <AutoAwesomeOutlinedIcon sx={{ fontSize: 15 }} />
                              : <AutorenewIcon sx={{ fontSize: 15 }} />
                            }
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
          rowsPerPageOptions={[10, 15, 25, 50]}
          sx={{
            borderTop: 1, borderColor: 'divider',
            '& .MuiTablePagination-toolbar': { minHeight: 48 },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: 13 },
          }}
        />
      </Box>
    </Box>
  );
}
